import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { BEAT_STATES } from "../../components/Dashboard/CredentialJourney/beatStates";
import { findTerminalExamBeat } from "../../components/Dashboard/CredentialJourney/terminalExam";
import axiosInstance from "../../utils/axiosInstance";

/**
 * credentialClearance - Act 1 clearance/journey read state (F1b B2).
 *
 * Backs the CredentialJourney strip + node surface. Fetches the shipped
 * `GET credential/capsules/{capsuleId}/clearance` read endpoint (F1a A1),
 * which returns Act-1 blocks[] (beats ordered by sequence within block-ordinal)
 * plus derived/stored act_state. Strictly a READ surface - mastery mutations
 * go through MasteryCheck's direct axiosInstance calls (QuizScreen precedent).
 *
 * IDENTITY RESET (platform self-review Q1): this slice is keyed by capsuleId.
 * When a fetch starts for a DIFFERENT capsule than the one currently held, the
 * pending reducer clears `data` so a switch between levels never renders a
 * stale level's beats. Combined with the AbortController the component passes
 * on unmount / capsuleId change (Q2), and the fulfilled/rejected stale-arg
 * guard below, a response for capsule A can never land into capsule B's view.
 *
 * SAME-CAPSULE OVERLAP (P1-4): the capsuleId guard alone cannot separate two
 * overlapping fetches for the SAME capsule (both carry the same arg). Every
 * dispatch gets a monotonic requestId (idGenerator); the pending reducer records
 * the latest one, and settles apply ONLY when their requestId is still the
 * latest - so an older response resolving last cannot clobber a newer payload.
 */
let clearanceRequestSeq = 0;

export const fetchClearance = createAsyncThunk(
  "credentialClearance/fetch",
  async ({ capsuleId, signal } = {}, thunkAPI) => {
    try {
      const response = await axiosInstance(
        `/credential/capsules/${capsuleId}/clearance`,
        "get",
        undefined,
        // Prefer the caller-supplied AbortSignal (component cleanup) and fall
        // back to createAsyncThunk's own signal so a dispatched-promise
        // .abort() still cancels the in-flight request. axiosInstance promotes
        // `signal` from customHeaders to top-level axios config (F2.2).
        { signal: signal ?? thunkAPI.signal },
      );
      return response.data.data;
    } catch (err) {
      // Surface the HTTP status so the page can redirect a 403/404 (opaque
      // not-authorized / not-a-credential-capsule) back to the dashboard
      // without leaking which case it was. Re-throw aborts so the rejected
      // reducer's `action.meta.aborted` guard still fires.
      if (err?.name === "CanceledError" || err?.name === "AbortError")
        throw err;
      return thunkAPI.rejectWithValue({
        status: err?.response?.status ?? null,
        message: "Failed to load clearance.",
      });
    }
  },
  // Monotonic request id so the slice can drop an older same-capsule settle.
  { idGenerator: () => `clr-${++clearanceRequestSeq}` },
);

/* ------------------------------------------------------------------ *
 * W3-F1: the session-only terminal transition marker.
 * ------------------------------------------------------------------ */

/**
 * The terminal beat's settled states. A transition INTO one of these, observed
 * while mounted, is what plays the credential challenge ending exactly once.
 *
 * `failed_terminal` IS NOT HERE AND ITS ABSENCE IS THE RULING (D10). That
 * branch never plays the sequence; `CompletedEnding` stands where it always
 * did. A sequence that played a shorter version of itself on a branch where the
 * credential was not earned would make the claim the branch exists to withhold.
 */
export const TERMINAL_SETTLED = Object.freeze([
  BEAT_STATES.CLEARED,
  BEAT_STATES.WAIVED,
]);

const isSettledTerminal = (state) =>
  state === BEAT_STATES.CLEARED || state === BEAT_STATES.WAIVED;

/**
 * The terminal exam beat's state in a clearance payload, or null.
 *
 * THROUGH THE SHIPPED PREDICATE, NEVER A SECOND ONE. "Which beat is the final
 * exam" is already answered once, in `terminalExam.js`, and it is a DERIVED
 * question (`evidence_kind === "mastery_pass"` with `blocks_progression ===
 * false`) rather than a flag on the row. A copy of that rule here would be a
 * second source of truth that can disagree with the first, which is the exact
 * defect `terminalExam.js`'s own docblock exists to prevent -- and the copy
 * would disagree SILENTLY, by never firing, because it would be looking for a
 * field the payload does not carry.
 *
 * The import direction is safe: `terminalExam.js` imports only `beatStates.js`
 * and `beatStates.js` imports nothing, so nothing here closes a cycle back
 * into the store.
 */
export const terminalStateOf = (data) =>
  findTerminalExamBeat(data)?.state ?? null;

const initialState = {
  capsuleId: null,
  /**
   * WHOSE CLEARANCE THIS IS (W3-F1, brief section 3 item 5; Codex round 4
   * finding 4).
   *
   * The slice keyed on `capsuleId` alone, and the dashboard cleared it on a
   * CHALLENGE change but not on a LEARNER change with the same capsule still
   * selected. On a shared browser -- a classroom machine, the case this product
   * is deployed into -- learner B's first cleared response was therefore
   * compared against learner A's non-settled state and recorded a transition
   * that never happened for B, playing A's ending at B. So identity is the
   * PAIR, and every comparison and every clear below reads both halves.
   *
   * SET BY ONE EXPLICIT ACTION, NEVER BY A THUNK ARGUMENT. Seven call sites
   * dispatch `fetchClearance` across the dashboard and the two training routes,
   * and a rule that all seven must remember to pass an extra field is a rule
   * that will be broken by the eighth. The Dashboard declares the authenticated
   * learner once, through `setClearanceIdentity`, and every read inherits it.
   */
  userId: null,
  /**
   * The identity that was current when the in-flight read was DISPATCHED.
   *
   * The take-latest `requestId` cannot answer "is this response still about the
   * right person": an account switch with no new fetch leaves the in-flight
   * request id as the latest one, so a response for the previous learner passes
   * every existing guard. Stamping the identity at dispatch and comparing it on
   * arrival is what drops it, and it does so inside the slice rather than
   * asking any of the seven callers to participate.
   */
  requestUserId: null,
  data: null,
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  errorStatus: null, // HTTP status on failure (403/404 → redirect, no leak)
  requestId: null, // latest in-flight request's monotonic id (P1-4 take-latest)
  /**
   * THE SESSION-ONLY, CONSUMABLE TERMINAL TRANSITION.
   *
   * `{ userId, capsuleId, to }` when a fulfilled response FOR THE SAME PAIR
   * changed the terminal beat from a non-settled state to cleared or waived,
   * and null otherwise. It is the rising edge and only the rising edge:
   *
   *   a cold load that arrives already settled records NOTHING, which is D9's
   *   "reload and back-navigation never replay";
   *   a staff waiver observed by a later authoritative refetch for the SAME
   *   learner IS a real transition and plays the waived ending;
   *   a response for the other account is dropped before it is compared.
   *
   * `CredentialSpine` consumes it exactly once. It is never persisted: the
   * slice is already outside the persist whitelist, so a reload starts with no
   * marker, which is the behaviour D9 asks for rather than a side effect of it.
   */
  terminalTransition: null,
};

const credentialClearanceSlice = createSlice({
  name: "credentialClearance",
  initialState,
  reducers: {
    // Explicit teardown when leaving the Act-1 surface family entirely, so a
    // later credential cohort never re-renders a prior cohort's clearance.
    clearCredentialClearance: () => initialState,
    /**
     * The marker is CONSUMED, not merely read (brief section 3 item 5).
     *
     * `CredentialSpine` emits the exam event once and then clears it here, so
     * the ending cannot replay on the next render, the next refetch, or a
     * back-navigation that remounts the spine. Consumption is keyed on the pair
     * the consumer actually saw: a marker that has already been replaced by a
     * newer identity is left alone rather than cleared by a late consumer that
     * was looking at the old one.
     */
    consumeTerminalTransition: (state, action) => {
      const seen = action.payload ?? {};
      const held = state.terminalTransition;
      if (!held) return;
      if (held.userId !== (seen.userId ?? null)) return;
      if (held.capsuleId !== (seen.capsuleId ?? null)) return;
      state.terminalTransition = null;
    },
    /**
     * Declare who this clearance belongs to.
     *
     * A CHANGE IS AN IDENTITY SWITCH AND CLEARS EVERYTHING PER-LEARNER. The
     * held payload goes, and so does any unconsumed marker: a transition
     * recorded for the account that just left is precisely the thing that would
     * play one learner's ending at another on a shared machine. The capsule is
     * deliberately NOT cleared -- it is the same level, and the read about to
     * run is about that level for the new learner.
     *
     * Dispatched from the Dashboard, which is where the authenticated learner
     * is known. Same value in, no-op out, so it is safe on every render.
     */
    setClearanceIdentity: (state, action) => {
      const nextUserId = action.payload?.userId ?? null;
      if (state.userId === nextUserId) return;
      /**
       * A READ DISPATCHED BEFORE ANYBODY WAS DECLARED IS ADOPTED, NOT ORPHANED
       * (fable-F1-m1).
       *
       * `requestUserId` is stamped at dispatch and compared on arrival, which
       * is what drops a response for an account the learner has left. It cannot
       * tell that case from a read that started before the identity was known:
       * `null !== 7` either way, so such a response is dropped, `status` stays
       * `loading` for ever, and nothing refetches. React runs CHILD effects
       * before PARENT effects, so a `CredentialSpine` mount fetch in the same
       * commit as the dashboard's identity effect is exactly that order.
       *
       * Reproducible at the slice level; not reachable through today's page
       * order, because the spine is not in the first commit on either a
       * bootstrap or a reload. That is an accident of mount order rather than a
       * guarantee, which is why it is repaired here rather than relied upon.
       *
       * The adoption is narrow ON PURPOSE: only an in-flight read that carried
       * NO identity at all. A read dispatched under a DIFFERENT learner is
       * still dropped, which is the shared-classroom case the pair exists for.
       */
      if (state.status === "loading" && state.requestUserId === null) {
        state.requestUserId = nextUserId;
      } else {
        /**
         * AND THE READ STATE GOES WITH THE PAYLOAD (cr-3961912845).
         *
         * `capsuleId` is deliberately kept -- it is the same level -- so a
         * `failed` left by the learner who just left was still `failed` for
         * the new learner on the same capsule, with `data`, `error` and
         * `errorStatus` all cleared. `CredentialTraining` and
         * `CredentialSpine` gate on `status === "failed"` and would render an
         * error, with nothing to say, for somebody whose read has not been
         * dispatched yet. `idle` is what they mean: nothing asked yet.
         *
         * The in-flight `requestId` goes too, because a response still out for
         * the previous learner must not be able to settle as the latest one.
         * The exception is the branch above, which ADOPTS an anonymous
         * in-flight read: that read is now this learner's, and dropping its id
         * would strand the response it is waiting for.
         */
        state.status = "idle";
        state.requestId = null;
      }
      state.userId = nextUserId;
      state.data = null;
      state.error = null;
      state.errorStatus = null;
      state.terminalTransition = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClearance.pending, (state, action) => {
        const nextId = action.meta.arg?.capsuleId ?? null;
        if (state.capsuleId !== nextId) {
          // Entity switched - drop the prior level's beats immediately, AND the
          // prior level's unconsumed marker with them: a transition recorded
          // for a capsule the learner has left must never play on the one they
          // have arrived at.
          state.data = null;
          state.error = null;
          state.errorStatus = null;
          state.capsuleId = nextId;
          state.terminalTransition = null;
        }
        // Stamp the identity this read was dispatched under. See
        // `requestUserId`: it is what drops a response for the previous account
        // when the switch happened while the read was already in flight.
        state.requestUserId = state.userId;
        // Latest request wins (P1-4): record this dispatch's id so a slower
        // earlier request for the SAME capsule is dropped when it settles.
        state.requestId = action.meta.requestId;
        state.status = "loading";
      })
      .addCase(fetchClearance.fulfilled, (state, action) => {
        // Stale-arg guard: ignore a response for a capsule we are no longer on.
        if ((action.meta.arg?.capsuleId ?? null) !== state.capsuleId) return;
        // ...OR FOR AN ACCOUNT WE ARE NO LONGER. The capsule guard alone cannot
        // separate two accounts on the same capsule, which is the shared-browser
        // case: without this, learner A's slower response lands into learner B's
        // slice and is compared against B's state.
        if (state.requestUserId !== state.userId) return;
        // Take-latest guard (P1-4): a superseded same-capsule response is dropped.
        if (action.meta.requestId !== state.requestId) return;

        /**
         * THE RISING EDGE, RECORDED BEFORE THE NEW DATA REPLACES THE OLD.
         *
         * `previous` has to be read from the state this response is about to
         * overwrite, so the comparison is between what this learner's screen
         * was showing and what the server now says. Three cases are separated
         * here rather than collapsed:
         *
         *   non-settled -> cleared/waived   a real transition. Record it.
         *   settled     -> settled          a cold load, or a refetch of an
         *                                   answer the learner already had.
         *                                   Records NOTHING (D9: no replay).
         *   anything    -> non-settled      not a transition at all.
         *
         * The waiver arm is deliberately not special-cased: a staff waiver
         * observed by a later authoritative refetch for the SAME learner IS a
         * rising edge, and the closed design plays the waived ending for it.
         */
        /**
         * A COLD LOAD HAS NO PREVIOUS STATE, AND THAT IS NOT THE SAME AS A
         * PREVIOUS STATE OF NOT-SETTLED.
         *
         * `state.data === null` means this slice has never held an answer for
         * this identity: the learner arrived on a page, or reloaded one, and
         * the first thing the server said was `cleared`. Nothing CHANGED --
         * the platform never showed them a non-settled terminal, so there was
         * no rising edge to observe.
         *
         * Without this the marker fires on every page load for the rest of the
         * challenge, and D9's "reload and back-navigation never replay" becomes
         * "the ending replays every time you open the dashboard". It was caught
         * by the cold-load test, which is the case a `!isSettledTerminal(null)`
         * check reads as a transition because `null` is, technically, not
         * settled.
         */
        const held = state.data !== null && state.data !== undefined;
        const previous = terminalStateOf(state.data);
        const next = terminalStateOf(action.payload);
        if (held && !isSettledTerminal(previous) && isSettledTerminal(next)) {
          state.terminalTransition = {
            userId: state.userId,
            capsuleId: state.capsuleId,
            to: next,
          };
        }

        state.data = action.payload;
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(fetchClearance.rejected, (state, action) => {
        // Aborted requests are not errors. `meta.aborted` only covers an abort
        // of createAsyncThunk's OWN signal; a component-supplied AbortController
        // (unmount / capsuleId change) surfaces as a re-thrown Canceled/Abort
        // error on action.error, which never sets meta.aborted. Ignore both so a
        // real navigation-away never flips the surface into a failed state.
        if (action.meta.aborted) return;
        if (
          action.error?.name === "CanceledError" ||
          action.error?.name === "AbortError"
        ) {
          return;
        }
        if ((action.meta.arg?.capsuleId ?? null) !== state.capsuleId) return;
        // The account half of the identity, exactly as on the fulfilled arm.
        if (state.requestUserId !== state.userId) return;
        // Take-latest guard (P1-4): a superseded same-capsule failure is dropped
        // so an older error can't override a newer in-flight request's outcome.
        if (action.meta.requestId !== state.requestId) return;
        state.status = "failed";
        state.error = action.payload?.message ?? "Failed to load clearance.";
        state.errorStatus = action.payload?.status ?? null;
      });
  },
});

export const {
  clearCredentialClearance,
  consumeTerminalTransition,
  setClearanceIdentity,
} = credentialClearanceSlice.actions;

export default credentialClearanceSlice.reducer;
